/**
 * <Tabs />
 *
 * Minimal, self-contained tab bar built to Parts Buoy's brand (thick
 * black/white with a red underline on the active tab).
 * Controlled -- parent owns `value` + `onChange`.
 */
import * as React from "react";
import { cn } from "../../lib/cn";

export interface TabItem {
  value: string;
  label: string;
  /** Hide this tab entirely. Useful for role-gating. */
  hidden?: boolean;
  /** Numeric badge (e.g. pending disputes). */
  count?: number;
}

interface Props {
  items: TabItem[];
  value: string;
  onChange: (v: string) => void;
  testid?: string;
  className?: string;
}

export function Tabs({ items, value, onChange, testid = "tabs", className }: Props) {
  const visible = items.filter((i) => !i.hidden);
  return (
    <div
      role="tablist"
      data-testid={testid}
      className={cn(
        "flex items-center gap-1 border-b-2 border-surface-border overflow-x-auto",
        className,
      )}
    >
      {visible.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            data-testid={`${testid}-tab-${item.value}`}
            onClick={() => onChange(item.value)}
            className={cn(
              "relative inline-flex items-center gap-2 whitespace-nowrap px-4 h-12 font-semibold text-sm transition-colors btn-target",
              active
                ? "text-ink"
                : "text-ink-muted hover:text-ink",
            )}
          >
            {item.label}
            {typeof item.count === "number" && item.count > 0 && (
              <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-black bg-brand text-white">
                {item.count}
              </span>
            )}
            {active && (
              <span
                aria-hidden="true"
                className="absolute left-2 right-2 -bottom-[2px] h-[3px] bg-brand rounded-t-sm"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}
